(function () {
  var EXPAND_DURATION = 700;
  var MOBILE_MQ = window.matchMedia('(max-width: 719px)');
  var instances = new WeakMap();
  var instancesBySectionId = {};
  var resizeTimer = null;

  var setExpandedState = function (section, isExpanded) {
    var expandButton = section.querySelector('[data-custom-story-banner-expand]');

    section.classList.toggle('is-expanded', isExpanded);

    if (expandButton) {
      expandButton.hidden = !isExpanded;
      expandButton.tabIndex = isExpanded ? 0 : -1;
      expandButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      expandButton.setAttribute(
        'aria-label',
        isExpanded ? 'Collapse image' : 'Expand image'
      );
    }
  };

  var applyExpandedState = function (section, isExpanded) {
    setExpandedState(section, isExpanded);
  };

  var clearInlineHeight = function (banner, shopifySection) {
    banner.style.height = '';
    banner.style.overflow = '';

    if (shopifySection) {
      shopifySection.style.height = '';
      shopifySection.style.overflow = '';
    }
  };

  var getSectionChrome = function (shopifySection) {
    if (!shopifySection) {
      return 0;
    }

    var styles = getComputedStyle(shopifySection);

    return parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
  };

  var getImageRatio = function (banner) {
    var styles = getComputedStyle(banner);
    var imageWidth = parseFloat(styles.getPropertyValue('--custom-story-banner-image-width')) || 4;
    var imageHeight = parseFloat(styles.getPropertyValue('--custom-story-banner-image-height')) || 5;

    return imageHeight / imageWidth;
  };

  var getCollapsedMediaHeight = function (banner) {
    return window.innerWidth * 0.8 * getImageRatio(banner);
  };

  var getExpandedBannerHeight = function (banner) {
    var styles = getComputedStyle(banner);
    var spaceOuter = parseFloat(styles.getPropertyValue('--space-outer')) || 16;

    return (window.innerWidth - 2 * spaceOuter) * getImageRatio(banner);
  };

  var getCardOverlap = function (banner, cache) {
    var mediaHeight = getCollapsedMediaHeight(banner);

    if (cache && cache.overlap) {
      var cachedMediaHeight = cache.mediaHeight || mediaHeight;

      if (cachedMediaHeight > 0) {
        return (cache.overlap / cachedMediaHeight) * mediaHeight;
      }

      return cache.overlap;
    }

    var content = banner.querySelector('.custom-story-banner__content');

    if (content) {
      var marginTop = parseFloat(getComputedStyle(content).marginTop);

      if (!isNaN(marginTop) && marginTop !== 0) {
        return Math.abs(marginTop);
      }
    }

    return 0;
  };

  var getCollapsedBannerHeight = function (banner, cache) {
    var mediaHeight = getCollapsedMediaHeight(banner);

    if (
      cache.banner &&
      cache.mediaHeight &&
      Math.abs(cache.mediaHeight - mediaHeight) < 1
    ) {
      return cache.banner;
    }

    var overlap = getCardOverlap(banner, cache);

    return mediaHeight + cache.content - overlap;
  };

  var cacheCollapsedHeights = function (banner, shopifySection, cache) {
    if (banner.classList.contains('is-expanded')) {
      return;
    }

    var content = banner.querySelector('.custom-story-banner__content');
    var bannerStyles = getComputedStyle(banner);
    var contentStyles = content ? getComputedStyle(content) : null;

    cache.content = content ? content.offsetHeight : 0;
    cache.mediaHeight = parseFloat(bannerStyles.paddingTop) || getCollapsedMediaHeight(banner);
    cache.overlap = contentStyles ? Math.abs(parseFloat(contentStyles.marginTop)) : 0;
    cache.banner = banner.offsetHeight;
    cache.section = shopifySection ? shopifySection.offsetHeight : cache.banner;
  };

  var runMobileHeightTransition = function (banner, nextExpanded, cache, onComplete) {
    var shopifySection = banner.closest('.shopify-section');
    var chrome = getSectionChrome(shopifySection);
    var expandedBannerHeight = getExpandedBannerHeight(banner);
    var collapsedBannerHeight = getCollapsedBannerHeight(banner, cache);
    var fromBannerHeight = nextExpanded ? cache.banner : expandedBannerHeight;
    var toBannerHeight = nextExpanded ? expandedBannerHeight : collapsedBannerHeight;
    var fromSectionHeight = nextExpanded ? cache.section : chrome + expandedBannerHeight;
    var toSectionHeight = nextExpanded ? chrome + expandedBannerHeight : chrome + collapsedBannerHeight;

    banner.style.height = fromBannerHeight + 'px';
    banner.style.overflow = 'hidden';

    if (shopifySection) {
      shopifySection.style.height = fromSectionHeight + 'px';
      shopifySection.style.overflow = 'hidden';
    }

    banner.classList.add('is-animating');

    if (shopifySection) {
      shopifySection.classList.add('is-animating');
    }

    applyExpandedState(banner, nextExpanded);

    void banner.offsetHeight;

    banner.style.height = toBannerHeight + 'px';

    if (shopifySection) {
      shopifySection.style.height = toSectionHeight + 'px';
    }

    var completed = false;

    var finish = function () {
      if (completed) {
        return;
      }

      completed = true;
      banner.removeEventListener('transitionend', onTransitionEnd);
      banner.classList.add('is-settling');
      banner.classList.remove('is-animating');

      if (shopifySection) {
        shopifySection.classList.remove('is-animating');
      }

      clearInlineHeight(banner, shopifySection);

      if (!nextExpanded) {
        cacheCollapsedHeights(banner, shopifySection, cache);
      }

      requestAnimationFrame(function () {
        banner.classList.remove('is-settling');
      });

      if (onComplete) {
        onComplete();
      }
    };

    var onTransitionEnd = function (event) {
      if (event.target !== banner || event.propertyName !== 'height') {
        return;
      }

      finish();
    };

    banner.addEventListener('transitionend', onTransitionEnd);
    window.setTimeout(finish, EXPAND_DURATION + 50);
  };

  var init = function (section) {
    if (!section) {
      return null;
    }

    var sectionId = section.dataset.sectionId;
    var shopifySection = section.closest('.shopify-section');
    var closeButton = section.querySelector('[data-custom-story-banner-close]');
    var expandButton = section.querySelector('[data-custom-story-banner-expand]');
    var listeners = [];
    var isAnimating = false;
    var collapsedHeights = {
      banner: 0,
      section: 0,
      content: 0,
      overlap: 0,
      mediaHeight: 0,
    };

    if (sectionId && instancesBySectionId[sectionId]) {
      instancesBySectionId[sectionId].destroy();
    }

    section.classList.remove('is-expanded', 'is-animating', 'is-settling');

    if (shopifySection) {
      shopifySection.classList.remove('is-animating');
    }

    clearInlineHeight(section, shopifySection);
    setExpandedState(section, false);
    cacheCollapsedHeights(section, shopifySection, collapsedHeights);

    var runExpandTransition = function (nextExpanded) {
      if (isAnimating) {
        return;
      }

      isAnimating = true;

      var finish = function () {
        isAnimating = false;
      };

      if (MOBILE_MQ.matches) {
        if (nextExpanded) {
          cacheCollapsedHeights(section, shopifySection, collapsedHeights);
        }

        runMobileHeightTransition(section, nextExpanded, collapsedHeights, finish);
        return;
      }

      applyExpandedState(section, nextExpanded);
      window.setTimeout(finish, EXPAND_DURATION);
    };

    if (closeButton) {
      var onClose = function () {
        runExpandTransition(true);
      };

      closeButton.addEventListener('click', onClose);
      listeners.push(function () {
        closeButton.removeEventListener('click', onClose);
      });
    }

    if (expandButton) {
      var onExpand = function () {
        runExpandTransition(!section.classList.contains('is-expanded'));
      };

      expandButton.addEventListener('click', onExpand);
      listeners.push(function () {
        expandButton.removeEventListener('click', onExpand);
      });
    }

    var onKeydown = function (event) {
      if (event.key !== 'Escape' || !section.classList.contains('is-expanded') || isAnimating) {
        return;
      }

      runExpandTransition(false);
    };

    document.addEventListener('keydown', onKeydown);
    listeners.push(function () {
      document.removeEventListener('keydown', onKeydown);
    });

    var instance = {
      destroy: function () {
        if (section.isConnected) {
          applyExpandedState(section, false);
        } else {
          setExpandedState(section, false);
        }

        section.classList.remove('is-animating', 'is-settling');

        if (shopifySection) {
          shopifySection.classList.remove('is-animating');
        }

        clearInlineHeight(section, shopifySection);

        listeners.forEach(function (unsubscribe) {
          unsubscribe();
        });

        instances.delete(section);

        if (sectionId && instancesBySectionId[sectionId] === instance) {
          delete instancesBySectionId[sectionId];
        }
      },
    };

    if (sectionId) {
      instancesBySectionId[sectionId] = instance;
    }

    instances.set(section, instance);

    return instance;
  };

  var destroy = function (section) {
    var instance = instances.get(section);

    if (instance) {
      instance.destroy();
      return;
    }

    var sectionId = section && section.dataset ? section.dataset.sectionId : null;

    if (sectionId && instancesBySectionId[sectionId]) {
      instancesBySectionId[sectionId].destroy();
    }
  };

  var initAll = function (scope) {
    var container = scope || document;

    container.querySelectorAll('[data-custom-story-banner]').forEach(function (section) {
      destroy(section);
      init(section);
    });
  };

  var scheduleReinit = function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      initAll();
    }, 150);
  };

  window.addEventListener('resize', scheduleReinit);

  if (typeof MOBILE_MQ.addEventListener === 'function') {
    MOBILE_MQ.addEventListener('change', scheduleReinit);
  } else if (typeof MOBILE_MQ.addListener === 'function') {
    MOBILE_MQ.addListener(scheduleReinit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target;

    scope.querySelectorAll('[data-custom-story-banner]').forEach(destroy);
  });
})();
